/**
 * =============================================================================
 * TEXTO CLINICO: COMO SE PINTA LO QUE ESCRIBE UN PROFESIONAL
 * =============================================================================
 * La descripcion clinica, la guia de intervencion y las contraindicaciones de
 * una sala las escribe una persona en un area de texto del panel de admin, con
 * sus parrafos y sus saltos de linea. Hasta el 2026-09-04 el portal las
 * pintaba en un <p> normal —donde un salto de linea es un espacio— o las
 * inyectaba como HTML —donde un salto de linea tampoco es nada—. Resultado:
 * mil caracteres "todo seguido", ilegibles para el terapeuta.
 *
 * Aqui se decide, con el texto en la mano, como pintarlo:
 *
 *   - Si trae etiquetas HTML (las guias que genero el auto-tagger vienen como
 *     <ul><li>...), se pinta como HTML, despues de quitarle lo que un HTML de
 *     catalogo no tiene por que llevar (scripts, manejadores de eventos,
 *     enlaces javascript:).
 *
 *   - Si es texto plano, se parte en parrafos por las lineas en blanco, se
 *     respetan los saltos de linea dentro de cada parrafo, y se entiende un
 *     marcado minimo: **negrita**, *cursiva* y listas con "- " o "1. ". Lo que
 *     no se reconoce se deja tal cual: es preferible un asterisco suelto a
 *     comerse texto clinico.
 *
 * Las funciones de este fichero son puras y sin React para poder congelarlas
 * con tests; el componente que las pinta esta en TextoClinico.js. (El fichero
 * no se llama textoClinico.js porque en Windows colisionaria con el del
 * componente: el sistema de ficheros no distingue mayusculas.)
 * =============================================================================
 */

/** true si el texto parece HTML (lleva al menos una etiqueta). */
export function esHtml(texto) {
  return /<\/?[a-z][^>]*>/i.test(String(texto || ""));
}

// -----------------------------------------------------------------------------
// HTML: lo que se quita antes de inyectarlo
// -----------------------------------------------------------------------------
// No es una libreria de saneado completa y no pretende serlo: el HTML viene de
// nuestra propia API, escrito por un admin o por el auto-tagger. Es una red
// por si algun dia llega algo que no deberia, no la defensa principal.

const BLOQUES_PELIGROSOS =
  /<(script|style|iframe|object|embed|form|noscript|template)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;
const ETIQUETAS_PELIGROSAS =
  /<\/?(script|style|iframe|object|embed|form|noscript|template|link|meta|base)\b[^>]*>/gi;
// Delante del atributo puede ir un espacio, una barra (<svg/onload=...>) o la
// comilla que cierra el atributo anterior (src="x"onerror=...). El separador
// capturado se conserva; el atributo, no.
const ATRIBUTOS_EVENTO = /([\s/"'])on[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi;
const URL_SCRIPT =
  /([\s/"'])(href|src|action|xlink:href|formaction)\s*=\s*("\s*javascript:[^"]*"|'\s*javascript:[^']*'|javascript:[^\s>]+)/gi;
const SRCDOC = /([\s/"'])srcdoc\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi;

export function sanearHtml(html) {
  return String(html || "")
    .replace(BLOQUES_PELIGROSOS, "")
    .replace(ETIQUETAS_PELIGROSAS, "")
    .replace(ATRIBUTOS_EVENTO, "$1")
    .replace(URL_SCRIPT, '$1$2="#"')
    .replace(SRCDOC, "$1");
}

/** Etiquetas que ya organizan el texto en bloques: si estan, los saltos de linea sobran. */
const TIENE_BLOQUES = /<(br|p|li|ul|ol|div|h[1-6]|table|tr|pre|blockquote)\b/i;

/**
 * El HTML listo para inyectar: saneado y, si solo trae marcado en linea
 * (<strong>, <em>, <a>...), con los saltos de linea convertidos en <br/>. Un
 * admin que escribe "<strong>Objetivo</strong>\nSegunda linea" espera ver dos
 * lineas, no una; en HTML un salto de linea no es nada.
 */
export function prepararHtml(html) {
  const limpio = sanearHtml(html);
  return TIENE_BLOQUES.test(limpio)
    ? limpio
    : limpio.replace(/\r\n?/g, "\n").replace(/\n/g, "<br/>");
}

// -----------------------------------------------------------------------------
// Texto plano con marcado minimo
// -----------------------------------------------------------------------------

/** "- algo", "* algo", "• algo", "1. algo", "2) algo" */
const VINETA = /^(?:[-*•·▪]|\d{1,2}[.)])\s+(.+)$/;

/** **negrita** o __negrita__, sin espacio pegado a los marcadores. */
const NEGRITA = /^(\*\*|__)(?=\S)([^\n]*?\S)\1/;

/** *cursiva* o _cursiva_, sin espacio pegado a los marcadores ni otro marcador dentro. */
const CURSIVA = /^(\*|_)(?=[^\s*_])([^*_\n]*?[^\s*_])\1(?![A-Za-z0-9À-ÿ*_])/;

/** Letra, cifra o marcador: si va justo antes, un * o _ no abre cursiva (5*3*2, snake_case). */
const PEGADO = /[A-Za-z0-9À-ÿ*_]/;

/**
 * Parte una linea en trozos { tipo: "texto" | "negrita" | "cursiva", valor }.
 *
 * Es un recorrido caracter a caracter, sin lookbehind en las expresiones: el
 * portal se abre en navegadores que no lo soportan y un regex que no compila
 * tira el bundle entero, no solo este componente.
 */
export function trocearInline(texto) {
  const partes = [];
  let plano = "";
  let resto = String(texto || "");

  const vuelca = () => {
    if (plano) {
      partes.push({ tipo: "texto", valor: plano });
      plano = "";
    }
  };

  while (resto.length) {
    const anterior = plano.slice(-1);
    const puedeAbrir = !anterior || !PEGADO.test(anterior);

    let m = resto.match(NEGRITA);
    if (m) {
      vuelca();
      partes.push({ tipo: "negrita", valor: m[2] });
      resto = resto.slice(m[0].length);
      continue;
    }

    m = puedeAbrir ? resto.match(CURSIVA) : null;
    if (m) {
      vuelca();
      partes.push({ tipo: "cursiva", valor: m[2] });
      resto = resto.slice(m[0].length);
      continue;
    }

    plano += resto[0];
    resto = resto.slice(1);
  }

  vuelca();
  return partes;
}

/**
 * Parte un texto plano en bloques:
 *   { tipo: "parrafo", lineas: [inline[]] }
 *   { tipo: "lista", ordenada: boolean, items: [inline[]] }
 *
 * Una linea en blanco cierra el bloque. Dentro de un parrafo, cada linea se
 * conserva (se pinta con <br/>). Las vinetas seguidas forman una lista.
 */
export function parsearTextoClinico(texto) {
  const lineas = String(texto || "")
    .replace(/\r\n?/g, "\n")
    .split("\n");

  const bloques = [];
  let actual = null;

  const cerrar = () => {
    if (actual) bloques.push(actual);
    actual = null;
  };

  lineas.forEach((cruda) => {
    const linea = cruda.trim();
    if (!linea) {
      cerrar();
      return;
    }

    const vineta = linea.match(VINETA);
    if (vineta) {
      const ordenada = /^\d/.test(linea);
      if (!actual || actual.tipo !== "lista" || actual.ordenada !== ordenada) {
        cerrar();
        actual = { tipo: "lista", ordenada, items: [] };
      }
      actual.items.push(trocearInline(vineta[1]));
      return;
    }

    if (!actual || actual.tipo !== "parrafo") {
      cerrar();
      actual = { tipo: "parrafo", lineas: [] };
    }
    actual.lineas.push(trocearInline(linea));
  });

  cerrar();
  return bloques;
}

/**
 * El texto sin marcado, en una sola linea. Para los sitios que solo ensenan un
 * resumen recortado (la tarjeta del catalogo), donde un "**" o un "<li>"
 * literal se leen peor que nada.
 */
export function aTextoPlano(texto) {
  return String(texto || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/(\*\*|__)(?=\S)([^\n]*?\S)\1/g, "$2")
    .replace(/^\s*(?:[-*•·▪]|\d{1,2}[.)])\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();
}
